import { ReactComponent as BookingsIcon } from '@/assets/icons/bookings.svg';
import { Card, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { BookingsList } from '@/components/shared/Booking/BookingsList';
import { getReservedBalance } from '@/components/shared/Booking/helper';
import {
  ReservedPointDetailModalBottomSheet,
} from '@/components/shared/SubwalletBalance/ReservedPointDetailModalBottomSheet';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ApiOrderTransaction, ApiSubwalletBalance, ApiUser } from '@api/interfaces';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Collapse, Stack, SxProps, Theme, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ReservedPointsCardProps {
  balances: ApiSubwalletBalance[],
  bookings: ApiOrderTransaction[],
  brandColor?: string,
  customer: ApiUser,
  forceMobileView?: boolean,
  hideBorder?: boolean,
  initialOpenModal?: boolean,
  linkedAccounts?: ApiUser[],
  sx?: SxProps<Theme>,
}

export const ReservedPointsCard = ({
  balances,
  bookings,
  brandColor,
  customer,
  forceMobileView = false,
  hideBorder = false,
  initialOpenModal = false,
  linkedAccounts,
  sx,
}: ReservedPointsCardProps) => {
  const { t } = useTranslation();
  const [isReservedPointsExpanded, setIsReservedPointsExpanded] = useState(false);
  const theme = useTheme();
  const { isMobile } = useMediaQuery();
  const [showBookingsModal, setShowBookingsModal] = useState(initialOpenModal);
  const showMobileView = forceMobileView || isMobile;

  const reservedPointsBookings = bookings.filter(
    (booking) => getReservedBalance(booking, balances) > 0,
  );

  if (!reservedPointsBookings.length) {
    return null;
  }

  return (
    <Stack sx={sx}>
      <Card
        elevation={0}
        onClick={() => (
          showMobileView
            ? setShowBookingsModal(true)
            : setIsReservedPointsExpanded(!isReservedPointsExpanded)
        )}
        sx={{
          border: 'none !important',
          boxShadow:
            hideBorder ?
              'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)',
          cursor: 'pointer',
        }}
        testID="reserved-wallet-modal"
      >
        <Stack>
          <Stack alignItems="center" direction="row" justifyContent="space-between">
            <Stack direction="row">
              <HSpacer size="2" />
              <BookingsIcon
                color={ brandColor ?? theme.palette.primary.main }
                height="24px"
                width="24px"
              />
              <HSpacer size="4" />
              <Stack justifyContent="center">
                <Text category="title-medium" sx={{ fontSize: '18px', lineHeight: '24px' }}>
                  {t($ => $.common.booking.reservedPoints)}
                </Text>
              </Stack>
            </Stack>
            <Stack alignItems="center" direction="row">
              <Text category="body-medium" sx={{ color: theme.palette.grey[700] }}>
                {t($ => $.common.booking.count, { count: reservedPointsBookings.length })}
              </Text>
              <HSpacer size="5" />
              {isReservedPointsExpanded
                ? <ExpandLess sx={{ color: theme.palette.grey[700] }} />
                : <ExpandMore sx={{ color: theme.palette.grey[700] }} />
              }
            </Stack>
          </Stack>
          <Collapse in={isReservedPointsExpanded} timeout="auto" unmountOnExit>
            <VSpacer size="4" />
            <BookingsList
              balances={balances}
              bookings={reservedPointsBookings}
              customer={customer}
              linkedAccounts={linkedAccounts}
            />
          </Collapse>
        </Stack>
      </Card>
      {showBookingsModal && (
        <ReservedPointDetailModalBottomSheet
          balances={balances}
          bookings={reservedPointsBookings}
          brandColor={brandColor}
          customer={customer}
          linkedAccounts={linkedAccounts}
          onClose={() => setShowBookingsModal(false)}
        />
      )}
    </Stack>
  );
};
